/**
 * API RTK Query pour le module Service Scolarité :
 * tâches (multi-assignés + ciblage pôle), commentaires, rapports journaliers,
 * rappels push (tâches + rapport journalier), stats admin par jour/pôle/agent.
 */
import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export type TypeTache = 'individuelle' | 'equipe';
export type PrioriteTache = 'normale' | 'haute' | 'urgente';
export type StatutTache = 'a_faire' | 'en_cours' | 'terminee' | 'annulee';

export interface CommentaireTache {
  id: number;
  auteur: number | null;
  auteur_nom: string;
  auteur_role: string;
  contenu: string;
  date_creation: string;
}

export interface AssigneSnapshot {
  id: number;
  nom: string;
  role?: string;
}

export interface TacheScolarite {
  id: number;
  titre: string;
  description: string;
  type_tache: TypeTache;
  priorite: PrioriteTache;
  statut: StatutTache;
  assignee_a: number | null;
  assignee_a_nom: string | null;
  assignees_ids: number[];
  assignees: AssigneSnapshot[];
  poles: string[];
  creee_par: number | null;
  creee_par_nom: string;
  date_echeance: string | null;
  date_creation: string;
  date_modification: string;
  date_completion: string | null;
  complete_par: number | null;
  complete_par_nom: string;
  est_en_retard: boolean;
  nb_commentaires: number;
  commentaires: CommentaireTache[];
  dernier_rappel_push: string | null;
}

export type TypeMessageRapport = 'commentaire' | 'action' | 'reponse_agent';
export type StatutAction = '' | 'a_faire' | 'en_cours' | 'faite' | 'abandonnee';

export interface CommentaireRapport {
  id: number;
  rapport: number;
  parent: number | null;
  auteur: number | null;
  auteur_nom: string;
  auteur_role: string;
  type_message: TypeMessageRapport;
  contenu: string;
  statut_action: StatutAction;
  deadline_action: string | null;
  action_terminee_le: string | null;
  action_terminee_par: number | null;
  lu_par_auteur_rapport: boolean;
  lu_par_admin: boolean;
  date_creation: string;
  date_modification: string;
  modifie: boolean;
  peut_modifier: boolean;
}

export interface RapportJournalier {
  id: number;
  auteur: number;
  auteur_nom: string;
  date_rapport: string;
  visibilite?: 'public' | 'prive';
  resume_libre: string;
  difficultes: string;
  besoins: string;
  taches_terminees_ids: number[];
  taches_terminees_titres: string[];
  lu_par_responsable: boolean;
  date_lecture: string | null;
  lu_par: number | null;
  date_creation: string;
  date_modification: string;
  auteur_poles: string[];
  commentaires?: CommentaireRapport[];
  nb_commentaires?: number;
  nb_actions_ouvertes?: number;
  nb_non_lus_pour_moi?: number;
}

export interface Agent {
  id: number;
  nom_complet: string;
  role: string;
  poles: string[];
}

export interface PoleItem {
  id: number;
  nom: string;
  code: string;
}

export interface StatsTaches {
  total: number;
  a_faire: number;
  en_cours: number;
  terminees: number;
  en_retard: number;
  urgentes: number;
}

export interface StatsRapports {
  depuis: string;
  jusqua: string;
  total_rapports: number;
  par_jour: { jour: string; nb_rapports: number; nb_lus: number }[];
  par_pole: { pole: string; nb_rapports: number }[];
  par_agent: {
    user_id: number;
    nom_complet: string;
    nb_rapports: number;
    nb_lus: number;
    poles: string[];
  }[];
}

export interface RappelStatus {
  doit_faire: boolean;
  nb_taches_du_jour: number;
  rapport_existe: boolean;
  seuil_heure: number;
  rapport: RapportJournalier | null;
}

export interface AgentsSansRapport {
  date: string;
  nb_manquants: number;
  agents: {
    user_id: number;
    nom_complet: string;
    role: string;
    poles: string[];
  }[];
}

export interface VerrouRapportStatus {
  verrouille: boolean;
  jusqu_a?: string;
  motif?: string;
  redirect_to?: string;
  dates_manquantes?: string[];
  taches_en_retard?: Array<{
    id: number;
    titre: string;
    priorite: 'normale' | 'haute' | 'urgente';
    date_echeance: string | null;
  }>;
  nb_taches_retard?: number;
  conditions_deblocage?: {
    rapport_a_faire: boolean;
    taches_a_completer: boolean;
    jour_ouvre_verifie: string;
  };
  unlock_admin_seul?: boolean;
}

export interface RapportReunion {
  id: number;
  titre: string;
  date_reunion: string;
  heure_debut: string | null;
  heure_fin: string | null;
  lieu: string;
  ordre_du_jour: string;
  resume: string;
  decisions: string;
  actions: string;
  participants_noms: string[];
  fichiers_urls: string[];
  cree_par: number | null;
  cree_par_nom: string;
  date_creation: string;
  date_modification: string;
}

export const serviceScolariteApi = createApi({
  reducerPath: 'serviceScolariteApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['Tache', 'Rapport', 'Agent', 'Pole', 'RappelStatus', 'Verrou', 'Reunion',
             'CommentairesRapport'],
  endpoints: (builder) => ({
    listTaches: builder.query<TacheScolarite[], {
      statut?: StatutTache;
      type_tache?: TypeTache;
      assignee_a?: number;
      pole?: string;
      en_retard?: boolean;
    } | void>({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params) {
          if (params.statut) usp.set('statut', params.statut);
          if (params.type_tache) usp.set('type_tache', params.type_tache);
          if (params.assignee_a) usp.set('assignee_a', String(params.assignee_a));
          if (params.pole) usp.set('pole', params.pole);
          if (params.en_retard) usp.set('en_retard', '1');
        }
        const qs = usp.toString();
        return `/service-scolarite/taches/${qs ? '?' + qs : ''}`;
      },
      transformResponse: (r: any) => r?.results ?? (Array.isArray(r) ? r : []),
      providesTags: ['Tache'],
    }),

    getTache: builder.query<TacheScolarite, number>({
      query: (id) => `/service-scolarite/taches/${id}/`,
      providesTags: ['Tache'],
    }),

    createTache: builder.mutation<TacheScolarite, Partial<TacheScolarite> & {
      assignees_ids?: number[]; poles?: string[];
    }>({
      query: (body) => ({ url: '/service-scolarite/taches/', method: 'POST', body }),
      invalidatesTags: ['Tache'],
    }),

    updateTache: builder.mutation<TacheScolarite, {
      id: number; data: Partial<TacheScolarite> & { assignees_ids?: number[]; poles?: string[] };
    }>({
      query: ({ id, data }) => ({ url: `/service-scolarite/taches/${id}/`, method: 'PATCH', body: data }),
      invalidatesTags: ['Tache'],
    }),

    deleteTache: builder.mutation<void, number>({
      query: (id) => ({ url: `/service-scolarite/taches/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Tache'],
    }),

    completerTache: builder.mutation<TacheScolarite, number>({
      query: (id) => ({ url: `/service-scolarite/taches/${id}/completer/`, method: 'POST' }),
      invalidatesTags: ['Tache', 'Rapport', 'RappelStatus'],
    }),

    rouvrirTache: builder.mutation<TacheScolarite, number>({
      query: (id) => ({ url: `/service-scolarite/taches/${id}/rouvrir/`, method: 'POST' }),
      invalidatesTags: ['Tache'],
    }),

    commenterTache: builder.mutation<CommentaireTache, { id: number; contenu: string }>({
      query: ({ id, contenu }) => ({
        url: `/service-scolarite/taches/${id}/commenter/`,
        method: 'POST',
        body: { contenu },
      }),
      invalidatesTags: ['Tache'],
    }),

    rappelerTache: builder.mutation<
      { ok: boolean; nb_destinataires: number; nb_pushs_envoyees: number },
      number
    >({
      query: (id) => ({ url: `/service-scolarite/taches/${id}/rappeler/`, method: 'POST' }),
      invalidatesTags: ['Tache'],
    }),

    mesTachesEnRetard: builder.query<{ count: number; taches: TacheScolarite[] }, void>({
      query: () => '/service-scolarite/taches/mes-en-retard/',
      providesTags: ['Tache'],
    }),

    statsTaches: builder.query<StatsTaches, void>({
      query: () => '/service-scolarite/taches/stats/',
      providesTags: ['Tache'],
    }),

    listRapports: builder.query<RapportJournalier[], {
      auteur?: number;
      depuis?: string;
      jusqua?: string;
      periode?: 'jour' | 'semaine';
      pole?: string;
      non_lus?: boolean;
    } | void>({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params) {
          if (params.auteur) usp.set('auteur', String(params.auteur));
          if (params.depuis) usp.set('depuis', params.depuis);
          if (params.jusqua) usp.set('jusqua', params.jusqua);
          if (params.periode) usp.set('periode', params.periode);
          if (params.pole) usp.set('pole', params.pole);
          if (params.non_lus) usp.set('non_lus', '1');
        }
        const qs = usp.toString();
        return `/service-scolarite/rapports/${qs ? '?' + qs : ''}`;
      },
      transformResponse: (r: any) => r?.results ?? (Array.isArray(r) ? r : []),
      providesTags: ['Rapport'],
    }),

    getRapportAujourdhui: builder.query<RapportJournalier, void>({
      query: () => '/service-scolarite/rapports/aujourdhui/',
      providesTags: ['Rapport', 'RappelStatus'],
    }),

    saveRapport: builder.mutation<RapportJournalier & { acces_debloque?: boolean; motif_verrou_residuel?: string }, {
      date_rapport?: string;
      resume_libre?: string;
      difficultes?: string;
      besoins?: string;
      visibilite?: 'public' | 'prive';
    }>({
      query: (body) => ({
        url: '/service-scolarite/rapports/',
        method: 'POST',
        body,
      }),
      // Invalider aussi 'Verrou' pour que VerrouRapportGuard re-fetche
      // immédiatement le statut après enregistrement — sans quoi l'overlay
      // reste bloqué jusqu'au prochain poll de 3 min.
      invalidatesTags: ['Rapport', 'RappelStatus', 'Verrou'],
    }),

    marquerRapportLu: builder.mutation<RapportJournalier, number>({
      query: (id) => ({ url: `/service-scolarite/rapports/${id}/marquer-lu/`, method: 'POST' }),
      invalidatesTags: ['Rapport'],
    }),

    monRappel: builder.query<RappelStatus, void>({
      query: () => '/service-scolarite/rapports/mon-rappel/',
      providesTags: ['RappelStatus'],
    }),

    agentsSansRapport: builder.query<AgentsSansRapport, { date?: string } | void>({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params?.date) usp.set('date', params.date);
        const qs = usp.toString();
        return `/service-scolarite/rapports/agents-sans-rapport/${qs ? '?' + qs : ''}`;
      },
      providesTags: ['Rapport'],
    }),

    rappelerAgentsRapport: builder.mutation<
      { ok: boolean; date: string; nb_destinataires: number; nb_pushs_envoyees: number },
      { user_ids?: number[]; date?: string } | void
    >({
      query: (body) => ({
        url: '/service-scolarite/rapports/rappeler-agents-rapport/',
        method: 'POST',
        body: body || {},
      }),
    }),

    statsRapports: builder.query<StatsRapports, { depuis?: string } | void>({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params?.depuis) usp.set('depuis', params.depuis);
        const qs = usp.toString();
        return `/service-scolarite/rapports/stats-periode/${qs ? '?' + qs : ''}`;
      },
      providesTags: ['Rapport'],
    }),

    listAgents: builder.query<Agent[], void>({
      query: () => '/service-scolarite/agents/',
      providesTags: ['Agent'],
    }),

    listPoles: builder.query<PoleItem[], void>({
      query: () => '/service-scolarite/poles/',
      providesTags: ['Pole'],
    }),

    // ── Verrou rapport journalier ─────────────────────────────────────
    checkVerrouRapport: builder.query<VerrouRapportStatus, void>({
      query: () => '/service-scolarite/verrou/',
      providesTags: ['Verrou'],
    }),
    leverVerrouRapport: builder.mutation<{ ok: boolean; user_id: number; nom: string }, number>({
      query: (user_id) => ({
        url: '/service-scolarite/verrou/lever/',
        method: 'POST',
        body: { user_id },
      }),
      invalidatesTags: ['Verrou'],
    }),

    listeComptesVerrouilles: builder.query<{
      jour_ouvre_reference: string;
      aujourdhui: string;
      verrouilles: Array<{
        user_id: number; nom_complet: string; role: string;
        jusqu_a: string; motif: string;
        nb_taches_retard: number;
        derniere_connexion: string | null;
      }>;
      nb_verrouilles: number;
      agents: Array<{
        user_id: number; nom_complet: string; role: string;
        verrouille: boolean; jusqu_a: string | null; motif: string;
        rapport_aujourdhui: boolean; rapport_jour_ouvre: boolean;
        nb_taches_retard: number;
        derniere_connexion: string | null;
      }>;
      nb_agents: number;
    }, void>({
      query: () => '/service-scolarite/verrou/liste-agents/',
      providesTags: ['Verrou'],
    }),

    // ── Rapports de réunion (visibles à tous) ─────────────────────────
    listReunions: builder.query<RapportReunion[], {
      depuis?: string; jusqua?: string; auteur?: number; q?: string;
    } | void>({
      query: (params) => {
        const usp = new URLSearchParams();
        if (params) {
          if (params.depuis) usp.set('depuis', params.depuis);
          if (params.jusqua) usp.set('jusqua', params.jusqua);
          if (params.auteur) usp.set('auteur', String(params.auteur));
          if (params.q) usp.set('q', params.q);
        }
        const qs = usp.toString();
        return `/service-scolarite/reunions/${qs ? '?' + qs : ''}`;
      },
      transformResponse: (r: any) => r?.results ?? (Array.isArray(r) ? r : []),
      providesTags: ['Reunion'],
    }),
    creerReunion: builder.mutation<RapportReunion, Partial<RapportReunion>>({
      query: (body) => ({
        url: '/service-scolarite/reunions/',
        method: 'POST', body,
      }),
      invalidatesTags: ['Reunion'],
    }),
    modifierReunion: builder.mutation<RapportReunion, { id: number; data: Partial<RapportReunion> }>({
      query: ({ id, data }) => ({
        url: `/service-scolarite/reunions/${id}/`,
        method: 'PATCH', body: data,
      }),
      invalidatesTags: ['Reunion'],
    }),
    supprimerReunion: builder.mutation<void, number>({
      query: (id) => ({
        url: `/service-scolarite/reunions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Reunion'],
    }),

    // ── Commentaires + actions sur un rapport journalier ───────────────
    listCommentairesRapport: builder.query<CommentaireRapport[], number>({
      query: (rapportId) => `/service-scolarite/rapports/${rapportId}/commentaires/`,
      providesTags: (r, e, rapportId) => [{ type: 'CommentairesRapport', id: rapportId }],
    }),
    ajouterCommentaireRapport: builder.mutation<CommentaireRapport, {
      rapportId: number;
      contenu: string;
      type_message?: TypeMessageRapport;
      parent?: number;
      deadline_action?: string;
    }>({
      query: ({ rapportId, ...body }) => ({
        url: `/service-scolarite/rapports/${rapportId}/commentaires/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (r, e, { rapportId }) => [
        { type: 'CommentairesRapport', id: rapportId },
        'Rapport',
      ],
    }),
    marquerCommentairesLus: builder.mutation<{ ok: boolean; nb_marques: number }, number>({
      query: (rapportId) => ({
        url: `/service-scolarite/rapports/${rapportId}/commentaires-marquer-lus/`,
        method: 'POST',
      }),
      invalidatesTags: (r, e, rapportId) => [
        { type: 'CommentairesRapport', id: rapportId },
        'Rapport',
      ],
    }),
    modifierCommentaireRapport: builder.mutation<CommentaireRapport, {
      id: number; contenu: string; rapportId: number;
    }>({
      query: ({ id, contenu }) => ({
        url: `/service-scolarite/commentaires-rapport/${id}/`,
        method: 'PATCH',
        body: { contenu },
      }),
      invalidatesTags: (r, e, { rapportId }) => [
        { type: 'CommentairesRapport', id: rapportId }, 'Rapport',
      ],
    }),
    supprimerCommentaireRapport: builder.mutation<void, { id: number; rapportId: number }>({
      query: ({ id }) => ({
        url: `/service-scolarite/commentaires-rapport/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (r, e, { rapportId }) => [
        { type: 'CommentairesRapport', id: rapportId }, 'Rapport',
      ],
    }),
    changerStatutAction: builder.mutation<CommentaireRapport, {
      id: number; statut_action: 'a_faire' | 'en_cours' | 'faite' | 'abandonnee';
      rapportId: number;
    }>({
      query: ({ id, statut_action }) => ({
        url: `/service-scolarite/commentaires-rapport/${id}/changer-statut/`,
        method: 'POST',
        body: { statut_action },
      }),
      invalidatesTags: (r, e, { rapportId }) => [
        { type: 'CommentairesRapport', id: rapportId }, 'Rapport',
      ],
    }),
  }),
});

export const {
  useListTachesQuery,
  useGetTacheQuery,
  useCreateTacheMutation,
  useUpdateTacheMutation,
  useDeleteTacheMutation,
  useCompleterTacheMutation,
  useRouvrirTacheMutation,
  useCommenterTacheMutation,
  useRappelerTacheMutation,
  useMesTachesEnRetardQuery,
  useStatsTachesQuery,
  useListRapportsQuery,
  useGetRapportAujourdhuiQuery,
  useSaveRapportMutation,
  useMarquerRapportLuMutation,
  useMonRappelQuery,
  useAgentsSansRapportQuery,
  useRappelerAgentsRapportMutation,
  useStatsRapportsQuery,
  useListAgentsQuery,
  useListPolesQuery,
  useCheckVerrouRapportQuery,
  useLeverVerrouRapportMutation,
  useListeComptesVerrouillesQuery,
  useListReunionsQuery,
  useCreerReunionMutation,
  useModifierReunionMutation,
  useSupprimerReunionMutation,
  useListCommentairesRapportQuery,
  useAjouterCommentaireRapportMutation,
  useMarquerCommentairesLusMutation,
  useModifierCommentaireRapportMutation,
  useSupprimerCommentaireRapportMutation,
  useChangerStatutActionMutation,
} = serviceScolariteApi;
